import {capitalize, isNull} from 'lodash-es';

import {spotheroGet, spotheroPost} from 'api';
import dayjs from 'utils/dayjs';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {formatPrice} from 'utils/number-format';
import {
    FLEX_RATES_CONFIG_DURATION_IN_MIN,
    generateRateSegmentDescription,
} from 'pages/rates/flex-rates/utils/utils';
import {DURATION_FULL_DAY, DURATION_HALF_DAY} from 'api/rates/constants';
import type {
    DayOfWeek,
    FlexRateApiError,
    FlexRateBand,
    FlexRateRawResponse,
    FlexRateFormattedRateBand,
    FlexRateFormattedRateSegment,
    FlexRateMatricesRawResponse,
    FlexRateMatricesResponse,
    FlexRateMatrixObject,
    FlexRateResponseData,
    FlexRateSegment,
    FlexRateSegmentInput,
    FlexRatesMatrices,
    SaveFlexRatesRequestData,
    FlexRateInterpolationAlgorithm,
} from './flex-rates.types';

/**
 * Formats the rate bands from the flex rates response
 * @param rateBands - The rate bands to format
 * @returns Formatted rate bands
 *
 * @group DTO Formatters
 */
const formatRateBands = (
    rateBands: FlexRateBand[]
): FlexRateFormattedRateBand[] => {
    const formattedRateBands = rateBands.reduce(
        (
            reformattedData: Record<string, FlexRateFormattedRateBand>,
            rateBand
        ) => {
            const {duration, daysOfWeek, rate} = rateBand;

            // first we check if the duration exists in the reformattedData object
            if (!reformattedData[duration]) {
                reformattedData[duration] = {duration};
            }

            // then we get the days of the week the rate band applies to and key the respective rate by day
            daysOfWeek.forEach((day) => {
                reformattedData[duration][day.toLowerCase()] =
                    formatPrice(rate);
            });

            return reformattedData;
        },
        {}
    );

    return Object.values(formattedRateBands);
};

/**
 * Formats the rate segments from the flex rates response
 * @param rateSegments - The rate segments to format
 * @returns Formatted rate segments
 *
 * @group DTO Formatters
 */
const formatRateSegments = (
    rateSegments: FlexRateSegment[]
): FlexRateFormattedRateSegment[] => {
    const formattedRateSegments = rateSegments.map((rateSegment) => {
        return {
            ...rateSegment,
            minRate: formatPrice(rateSegment.minRate),
            maxRate: formatPrice(rateSegment.maxRate),
            // Cast needed: generateRateSegmentDescription is a JS file with incorrect JSDoc param types
            description: generateRateSegmentDescription(
                rateSegment as unknown as Parameters<
                    typeof generateRateSegmentDescription
                >[0]
            ),
        };
    });

    return formattedRateSegments;
};

/**
 * Formats the flex rates response
 * @param data - The raw API response data
 * @param isAirportPage - Whether to filter for airport page
 * @returns Formatted flex rate response data
 *
 * @group DTO Formatters
 */
const formatFlexRatesResponse = (
    data: FlexRateRawResponse,
    isAirportPage?: boolean
): FlexRateResponseData => {
    const formattedResponse = camelizeKeys(data);
    const {config} = formattedResponse;
    const rateBands = isAirportPage
        ? config.rateBands.filter(
              (band) =>
                  band.duration === DURATION_FULL_DAY ||
                  band.duration === DURATION_HALF_DAY
          )
        : config.rateBands;

    return {
        ...formattedResponse,
        config: {
            ...config,
            interpolation:
                config.interpolation.algorithm.toLowerCase() as Lowercase<FlexRateInterpolationAlgorithm>,
            rateBands: formatRateBands(rateBands),
            rateSegments: formatRateSegments(config.rateSegments),
            rateTriggeredSegments: formatRateSegments(
                config.rateTriggeredSegments
            ),
        },
    };
};

/**
 * Gets the flex rates for a rule group
 * @param ruleGroupId - The id of the rule group
 * @param isAirportPage - Whether to show on the Airport page
 * @returns Promise resolving to formatted flex rate data
 *
 * @throws {Error}
 *
 * @see '/api/v1/facilities/rates/rule-groups/{ruleGroupId}/flex-rates/'
 */
export const getFlexRates = async (
    ruleGroupId: number,
    isAirportPage: boolean
): Promise<FlexRateResponseData> => {
    try {
        const response = await spotheroGet<FlexRateRawResponse>(
            `/facilities/rates/rule-groups/${ruleGroupId}/flex-rates/`
        );

        const {data} = response;
        const formattedResponse = formatFlexRatesResponse(
            data.data,
            isAirportPage
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats the rate bands request by grouping days with the same rate and formatting the rate value.
 * @param rateBands - The rate bands data to be formatted
 * @returns The formatted rate bands data for API request
 *
 * @group DTO Formatters
 */
const formatRateBandsRequest = (rateBands: FlexRateFormattedRateBand[]) => {
    const formattedRateBands = rateBands.reduce(
        (arr: FlexRateBand[], rateBand) => {
            const {duration, ...daysWithRate} = rateBand;
            // Create an object to group days by rate
            const rateGroups: Record<string, DayOfWeek[]> = {};

            for (const [day, rate] of Object.entries(daysWithRate)) {
                // if no rate is set, then skip it
                if (!isNull(rate)) {
                    const rateKey = String(rate);

                    // If the rate group does not exist, create an empty array
                    if (!rateGroups[rateKey]) {
                        rateGroups[rateKey] = [];
                    }
                    rateGroups[rateKey].push(capitalize(day) as DayOfWeek);
                }
            }

            for (const [rate, daysOfWeek] of Object.entries(rateGroups)) {
                // Push the formatted rate band to the result array
                arr.push({
                    duration,
                    daysOfWeek,
                    rate: formatPrice(rate, {
                        formatterCallback: Number,
                        toCents: true,
                    }),
                });
            }

            return arr;
        },
        []
    );

    return formattedRateBands;
};

/**
 * Formats rate segments data for an API request by separating the rate segments from the rate triggered segments and formatting the rate values.
 * @param rateSegments - The rate segments data to be formatted
 * @returns The formatted rate segments and rate triggered segments data for the API request
 *
 * @group DTO Formatters
 */
const formatRateSegmentsRequest = (rateSegments: FlexRateSegmentInput[]) => {
    const formattedRateSegments = rateSegments.reduce(
        (
            acc: {
                rateSegments: FlexRateSegmentInput[];
                rateTriggeredSegments: FlexRateSegmentInput[];
            },
            rateSegment
        ) => {
            // handle segments with a trigger type
            if (rateSegment.triggerType) {
                acc.rateTriggeredSegments.push({
                    ...rateSegment,
                    minRate: formatPrice(rateSegment.minRate, {
                        formatterCallback: Number,
                        toCents: true,
                    }),
                    maxRate: formatPrice(rateSegment.maxRate, {
                        formatterCallback: Number,
                        toCents: true,
                    }),
                });
            } else {
                acc.rateSegments.push({
                    ...rateSegment,
                    minRate: formatPrice(rateSegment.minRate, {
                        formatterCallback: Number,
                        toCents: true,
                    }),
                    maxRate: formatPrice(rateSegment.maxRate, {
                        formatterCallback: Number,
                        toCents: true,
                    }),
                });
            }

            return acc;
        },
        {rateSegments: [], rateTriggeredSegments: []}
    );

    return formattedRateSegments;
};

/**
 * Formats the flex rates request
 * @param data - The data containing rateBands, rateSegments, and interpolation
 * @returns Formatted request data with snake_case keys
 *
 * @group DTO Formatters
 */
const formatSaveFlexRatesRequest = (data: SaveFlexRatesRequestData) => {
    const rateBands = formatRateBandsRequest(data.rateBands);
    const {rateSegments, rateTriggeredSegments} = formatRateSegmentsRequest(
        data.rateSegments
    );

    const formattedData = {
        interpolation: {algorithm: capitalize(data.interpolation)},
        // TODO:  Remove this override once the facilities have been migrated over to 30 min configs. @see https://spothero.atlassian.net/browse/SIQ-1263
        durationInMin: FLEX_RATES_CONFIG_DURATION_IN_MIN['30min'],
        rateBands,
        rateSegments,
        rateTriggeredSegments,
    };

    const formattedRequest = convertKeysToSnakeCase(formattedData);

    return formattedRequest;
};

/**
 * Saves and publish flex rates data to the server.
 * @param data - The flex rates data to be saved
 * @param ruleGroupId - The ID of the rule group to associate the flex rates with
 * @returns Promise resolving to the formatted flex rates response
 *
 * @throws {Error}
 *
 * @see '/api/v1/facilities/rates/rule-groups/{ruleGroupId}/flex-rates/'
 */
export const saveFlexRates = async (
    data: SaveFlexRatesRequestData,
    ruleGroupId: number
): Promise<FlexRateResponseData> => {
    try {
        const formattedRequest = formatSaveFlexRatesRequest(data);

        const response = await spotheroPost<FlexRateRawResponse>(
            `/facilities/rates/rule-groups/${ruleGroupId}/flex-rates/`,
            formattedRequest,
            {},
            false
        );

        const {data: responseData} = response;
        const formattedResponse = formatFlexRatesResponse(responseData.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        const formattedError =
            (error as FlexRateApiError)?.data?.data?.errors?.[0] || error;

        throw formattedError;
    }
};

/**
 * Formats the flex rates matrices response for a 30min config to a format consumable by the heatmap chart
 * @param formattedResponse - The formatted response data
 * @param xLabels - The x-axis labels
 * @param yLabels - The y-axis labels
 * @returns Formatted matrices data
 *
 * @group DTO Formatters
 */
const formatFlexRatesMatrices30MinResponse = (
    formattedResponse: Record<string, FlexRateMatrixObject[][]>,
    xLabels: number[],
    yLabels: string[]
): FlexRatesMatrices => {
    const formattedData = Object.keys(formattedResponse).reduce(
        (acc: FlexRatesMatrices, day) => {
            const formattedDay = day.toLowerCase();

            // handle the 30 min configs otherwise handle the 1 hour configs
            acc[formattedDay] = formattedResponse[day]
                // filter out the even indices to get the 30 min configs as a default
                .filter((_, i) => i % 2 === 0)
                .map((result, i) => {
                    return {
                        id: yLabels[i],
                        data: result
                            // filter out the odd indices to get the 60 min durations since the even indices are the 30 min durations.  This is so we only display rates from the top of each hour (0 index would be 00:30).
                            .filter((_, v) => v % 2 !== 0)
                            .map((value, v) => {
                                return {
                                    ...value.source,
                                    x: xLabels[v],
                                    y: value.rate,
                                    searchable: value.searchable,
                                };
                            }),
                    };
                });

            return acc;
        },
        {}
    );

    return formattedData;
};

/**
 * Formats the flex rates matrices response for a 60min config to a format consumable by the heatmap chart
 * @returns Formatted matrices data
 *
 * @group DTO Formatters
 */
const formatFlexRatesMatrices60MinResponse = (
    formattedResponse: Record<string, FlexRateMatrixObject[][]>,
    xLabels: number[],
    yLabels: string[]
): FlexRatesMatrices => {
    const formattedData = Object.keys(formattedResponse).reduce(
        (acc: FlexRatesMatrices, day) => {
            const formattedDay = day.toLowerCase();

            acc[formattedDay] = formattedResponse[day].map((result, i) => {
                return {
                    id: yLabels[i],
                    data: result.map((value, v) => {
                        return {
                            ...value.source,
                            x: xLabels[v],
                            y: value.rate,
                            searchable: value.searchable,
                        };
                    }),
                };
            });

            return acc;
        },
        {}
    );

    return formattedData;
};

/**
 * Formats the flex rates matrices response to a format consumable by the heatmap chart
 * @returns Formatted matrices data
 *
 * @group DTO Formatters
 */
const formatFlexRatesMatricesResponse = (
    data: FlexRateMatricesRawResponse
): FlexRatesMatrices => {
    const formattedResponse = camelizeKeys<
        FlexRateMatricesRawResponse,
        FlexRateMatricesResponse
    >(data);
    const {durationInMin, ...dayMatrices} = formattedResponse;
    // create an array called xLabels that contain numbers 1-24
    const xLabels = Array.from(Array(24).keys()).map((i) => i + 1);
    // create an array called yLabels that contain hourly durations from 0:00-23:00
    const yLabels = Array.from(Array(24).keys()).map((i) => {
        return dayjs(`${i}`, 'H').format('h:mma');
    });

    // handle the 30 min configs
    if (durationInMin === FLEX_RATES_CONFIG_DURATION_IN_MIN['30min']) {
        return formatFlexRatesMatrices30MinResponse(
            dayMatrices,
            xLabels,
            yLabels
        );
    }

    // handle the 60 min configs as a default
    return formatFlexRatesMatrices60MinResponse(dayMatrices, xLabels, yLabels);
};

/**
 * Gets the flex rates matrices for a collection of flex rates
 * @param flexRateId - The id of the flex rate
 * @returns Promise resolving to formatted matrices data
 *
 * @throws {Error}
 *
 * @see '/api/v1/facilities/rates/flex-rates/{flexRateId}/matrices/'
 */
export const getFlexRatesMatrices = async (
    flexRateId: number
): Promise<FlexRatesMatrices> => {
    try {
        const response = await spotheroGet<FlexRateMatricesRawResponse>(
            `/facilities/rates/flex-rates/${flexRateId}/matrices/`
        );

        const formattedResponse = formatFlexRatesMatricesResponse(
            response?.data?.data
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Generates a preview for the flex rates matrices for a collection of flex rates
 * @param data - The flex rates data containing interpolation, rateBands, and rateSegments
 * @returns Promise resolving to formatted matrices data
 *
 * @see '/api/v1/facilities/rates/flex-rates/generate/'
 */
export const previewFlexRatesMatrix = async ({
    rateBands,
    rateSegments,
    interpolation,
}: SaveFlexRatesRequestData): Promise<FlexRatesMatrices> => {
    try {
        const formattedRequest = formatSaveFlexRatesRequest({
            rateBands,
            rateSegments,
            interpolation,
        });

        const response = await spotheroPost<FlexRateMatricesRawResponse>(
            '/facilities/rates/flex-rates/generate/',
            formattedRequest,
            {},
            false
        );

        const formattedResponse = formatFlexRatesMatricesResponse(
            response?.data?.data
        );

        return formattedResponse;
    } catch (error) {
        const formattedError =
            (error as FlexRateApiError)?.response?.data?.data?.errors?.[0] ||
            error;

        throw formattedError;
    }
};
